/* Native homepage tracks, enhanced near the viewport with shared slim controls. */
(() => {
  if (window.hpdWeb007ScrollMounted) return;
  window.hpdWeb007ScrollMounted = true;
  const trackSelector = '[id^="Slider-"].slider.product-grid, [id^="Slider-"].slider.collection-list';
  if (!customElements.get('hpd-web007-scroll')) {
    customElements.define('hpd-web007-scroll', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.host = this.parentElement;
        this.track = this.host?.querySelector(trackSelector);
        if (!this.track) return;
        this.controller = new AbortController();
        this.stops = [0]; this.frame = 0; this.goal = null;
        this.needsMeasure = true; this.observed = new Set();
        const label = this.dataset.label || 'Browse items';
        const button = (glyph, name) => {
          const node = document.createElement('button');
          node.type = 'button'; node.className = 'hpd-scroll-arrow'; node.textContent = glyph;
          node.setAttribute('aria-label', `${name}: ${label}`);
          node.setAttribute('aria-controls', this.track.id);
          return node;
        };
        this.previous = button('<', 'Previous items');
        this.next = button('>', 'Next items');
        this.input = document.createElement('input');
        Object.assign(this.input, {type:'range', min:'0', max:'1', step:'1', value:'0'});
        this.input.setAttribute('aria-label', label);
        this.input.setAttribute('aria-controls', this.track.id);
        this.replaceChildren(this.previous, this.input, this.next);
        const options = {signal:this.controller.signal};
        this.previous.addEventListener('click', () => this.move(-1), options);
        this.next.addEventListener('click', () => this.move(1), options);
        this.input.addEventListener('input', () => {
          this.goal = null;
          this.track.scrollTo({left:this.sign * (this.stops[Number(this.input.value)] || 0), behavior:'instant'});
        }, options);
        this.track.addEventListener('scroll', () => this.schedule(), {...options, passive:true});
        for (const type of ['pointerdown', 'wheel', 'keydown']) {
          this.track.addEventListener(type, () => { this.goal = null; }, {...options, passive:true});
        }
        window.addEventListener('resize', () => this.schedule(true), options);
        this.resize = new ResizeObserver(() => this.schedule(true));
        this.resize.observe(this.track);
        this.childrenObserver = new MutationObserver(() => { this.observeCards(); this.schedule(true); });
        this.childrenObserver.observe(this.track, {childList:true});
        this.observeCards();
        this.update();
        this.host.classList.add('hpd-web007-scroll-ready');
      }
      observeCards() {
        const children = new Set(this.track.children);
        this.observed.forEach(child => {
          if (!children.has(child)) { this.resize.unobserve(child); this.observed.delete(child); }
        });
        children.forEach(child => {
          if (!this.observed.has(child)) { this.observed.add(child); this.resize.observe(child); }
        });
      }
      schedule(measure = false) {
        this.needsMeasure ||= measure;
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      }
      update() {
        if (!this.isConnected || !this.track?.isConnected) return;
        if (this.needsMeasure) {
          this.needsMeasure = false; this.goal = null;
          this.max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
          this.sign = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
          const origin = this.track.firstElementChild?.offsetLeft || 0;
          this.stops = Array.from(new Set([0, ...Array.from(this.track.children, child =>
            Math.max(0, Math.min(this.max, Math.abs(child.offsetLeft - origin)))), this.max])).sort((a,b) => a-b);
          this.input.max = String(Math.max(1, this.stops.length - 1));
          this.previous.textContent = this.sign < 0 ? '>' : '<';
          this.next.textContent = this.sign < 0 ? '<' : '>';
        }
        const position = Math.max(0, Math.min(this.max, this.sign * this.track.scrollLeft));
        if (this.goal !== null && Math.abs(this.goal - position) < 2) this.goal = null;
        this.hidden = this.max < 2;
        this.input.disabled = this.max < 2;
        this.previous.disabled = position < 2;
        this.next.disabled = this.max - position < 2;
        this.input.value = String(this.stops.reduce((best, value, index) =>
          Math.abs(value-position) < Math.abs(this.stops[best]-position) ? index : best, 0));
        this.input.setAttribute('aria-valuetext', `${Math.round(this.max ? position / this.max * 100 : 0)}% through collection`);
      }
      move(direction) {
        if (this.needsMeasure) this.update();
        const position = this.goal ?? Math.max(0, this.sign * this.track.scrollLeft);
        const target = direction > 0 ? this.stops.find(value => value > position + 2) ?? this.max :
          [...this.stops].reverse().find(value => value < position - 2) ?? 0;
        this.goal = target;
        this.track.scrollTo({left:this.sign * target,
          behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      }
      disconnectedCallback() {
        this.controller?.abort(); this.controller = null;
        this.resize?.disconnect(); this.childrenObserver?.disconnect();
        cancelAnimationFrame(this.frame); this.frame = 0;
        this.host?.classList.remove('hpd-web007-scroll-ready');
      }
    });
  }
  const pending = new Set();
  const initialize = host => {
    pending.delete(host); lazy?.unobserve(host);
    if (!host.isConnected || host.querySelector('hpd-web007-scroll')) return;
    const section = host.closest('.shopify-section');
    const heading = section?.querySelector('h2')?.textContent.trim();
    const coastal = section?.id.endsWith('__featured_collection_bDRnmT');
    const bar = document.createElement('hpd-web007-scroll');
    bar.dataset.label = `Browse ${heading || (coastal ? 'Coastal Vibes products' :
      host.querySelector('.product-grid') ? 'products' : 'collections')}`;
    host.append(bar);
  };
  const lazy = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) initialize(entry.target); });
  }, {rootMargin:'600px'}) : null;
  const mount = (root = document) => {
    if (!document.querySelector('#MainContent hpd-home-slideshow') &&
        location.pathname !== (window.Shopify?.routes?.root || '/')) return;
    root.querySelectorAll('#MainContent slider-component, slider-component').forEach(host => {
      if (!host.closest('#MainContent') || !host.querySelector(trackSelector) ||
          host.querySelector('hpd-web007-scroll') || pending.has(host)) return;
      if (lazy) { pending.add(host); lazy.observe(host); }
      else initialize(host);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), {once:true});
  else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    pending.forEach(host => {
      if (event.target.contains(host) || !host.isConnected) { lazy?.unobserve(host); pending.delete(host); }
    });
  });
  window.addEventListener('pageshow', () => {
    mount(); document.querySelectorAll('hpd-web007-scroll').forEach(bar => bar.schedule(true));
  });
})();

/* Label the observed Etsy attribution graphic without changing provider content. */
(() => {
  if (window.hpdWeb007ReviewIconReady) return;
  window.hpdWeb007ReviewIconReady = true;
  const scope = '[id$="__170080004506d24bce"]';
  const selector = 'i.fsl-leading-none[aria-label="Powered by Etsy"][title="Powered by Etsy"]:not([role])';
  const observers = new Map();
  const enhance = root => {
    const icons = root.matches?.(selector) ? [root] : [];
    icons.push(...root.querySelectorAll(selector));
    icons.forEach(icon => {
      if (icon.querySelector('svg') && !icon.querySelector('a,button,input,select,textarea,[tabindex]')) {
        icon.setAttribute('role', 'img');
      }
    });
  };
  const start = (parent = document) => {
    const roots = parent.matches?.(scope) ? [parent] : [];
    roots.push(...parent.querySelectorAll(scope));
    roots.forEach(root => {
      enhance(root);
      if (observers.has(root)) return;
      const observer = new MutationObserver(records => {
        records.forEach(record => record.addedNodes.forEach(node => {
          if (node.nodeType === 1) enhance(node);
        }));
      });
      observer.observe(root, {childList:true, subtree:true});
      observers.set(root, observer);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start(), {once:true});
  else start();
  document.addEventListener('shopify:section:load', event => start(event.target));
  document.addEventListener('shopify:section:unload', event => {
    observers.forEach((observer, root) => {
      if (event.target.contains(root) || !root.isConnected) { observer.disconnect(); observers.delete(root); }
    });
  });
  window.addEventListener('pageshow', () => start());
})();
